/**
 * Main App Component — Routes for client portal + admin panel
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useAdmin } from './hooks/useAdmin';
import { ImpersonationProvider } from './hooks/useImpersonation';
import { RehearsalBanner } from './components/Layout/RehearsalBanner';
import { Login } from './components/Auth/Login';
import { VerifyEmail } from './components/Auth/VerifyEmail';
import { Dashboard } from './components/Dashboard/Dashboard';
import { Leads } from './components/Leads/Leads';
import { Results } from './components/Dashboard/Results';
import { ScheduleCalls } from './components/Dashboard/ScheduleCalls';
import { ConfigureOrder } from './components/BuyLeads/ConfigureOrder';
import { AdminDashboard } from './components/Admin/AdminDashboard';
import { CreateClient } from './components/Admin/CreateClient';
import { ManageCredits } from './components/Admin/ManageCredits';
import { CreateAgent } from './components/Admin/CreateAgent';
import { ManageClient } from './components/Admin/ManageClient';
import { VerificationJobs } from './components/Admin/VerificationJobs';
import { AdminImdata } from './components/Admin/AdminImdata';
import { SupportTickets } from './components/Admin/SupportTickets';
import { PromoCodes } from './components/Admin/PromoCodes';
import { HelpCenter } from './components/Help/HelpCenter';
import { VersionUpdateToast } from './components/Layout/VersionUpdateToast';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, emailVerified } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();

  if (loading || adminLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admins bypass email verification requirement
  if (!emailVerified && !isAdmin) {
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();

  if (authLoading || adminLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Client Routes */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/schedule-calls" element={<ProtectedRoute><ScheduleCalls /></ProtectedRoute>} />
      <Route path="/buy-leads/configure" element={<ProtectedRoute><ConfigureOrder /></ProtectedRoute>} />
      <Route path="/results/:type" element={<ProtectedRoute><Results /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/create-client" element={<AdminRoute><CreateClient /></AdminRoute>} />
      <Route path="/admin/create-agent" element={<AdminRoute><CreateAgent /></AdminRoute>} />
      <Route path="/admin/client/:clientId" element={<AdminRoute><ManageClient /></AdminRoute>} />
      <Route path="/admin/credits/:clientId" element={<AdminRoute><ManageCredits /></AdminRoute>} />
      <Route path="/admin/verification-jobs" element={<AdminRoute><VerificationJobs /></AdminRoute>} />
      <Route path="/admin/imdata" element={<AdminRoute><AdminImdata /></AdminRoute>} />
      <Route path="/admin/tickets" element={<AdminRoute><SupportTickets /></AdminRoute>} />
      <Route path="/admin/promo-codes" element={<AdminRoute><PromoCodes /></AdminRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ImpersonationProvider>
        <BrowserRouter>
          <AppRoutes />
          {/* Sits outside <Routes> so the toast survives client-side
              navigation — a stale tab that's been left on /schedule-calls
              for an hour gets the same prompt as one on /. */}
          <VersionUpdateToast />
          {/* Local dev only (never in a build): says whether this portal is on the
              emulators (rehearsal) or pointed at the live system. */}
          <RehearsalBanner />
        </BrowserRouter>
      </ImpersonationProvider>
    </AuthProvider>
  );
}
